import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Card } from "../../components/Card.tsx";
import InputErrorHelper from "../../components/InputErrorHelper.tsx";
import { useORPC } from "../../orpc-react.tsx";

export interface ImportProps {
  /**
   * If provided, we can explain to the user how they grant us read access to a
   * private spreadsheet using this service account.
   */
  applicationServiceAccountClientEmail?: string;
}

export default function Import(props: ImportProps): JSX.Element {
  const { applicationServiceAccountClientEmail } = props;
  const orpc = useORPC();
  const importFromSheet = useMutation(orpc.accounting.importAccounts.mutationOptions());

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = useForm({
    resolver: zodResolver(
      z.object({
        accountSheetName: z
          .string()
          .trim()
          .optional()
          .transform((name): string | undefined => name || undefined),
        sourceSpreadsheetId: z
          .string()
          .trim()
          .nonempty({ error: "The spreadsheet identifier cannot be empty." }),
      }),
    ),
    mode: "onBlur",
    shouldUseNativeValidation: false,
  });
  const validateAndSendImportRequest = handleSubmit((importRequest, _) => {
    importFromSheet.mutateAsync(importRequest);
  });

  return (
    <>
      <Card title="Import from sheets">
        {applicationServiceAccountClientEmail ? (
          <p>
            In order to access your spreadsheet, you can either make it temporarily public read-only
            or grant view access to this application's service account{" "}
            <code>luna-rs@heliopolis.iam.gserviceaccount.com</code>
          </p>
        ) : (
          <p>
            In order to access your spreadsheet, we will need you to make read access public. You
            can revoke the access immediately after the import has completed.
          </p>
        )}
        <form onSubmit={validateAndSendImportRequest}>
          <div className="form-control">
            <label htmlFor="sourceSpreadsheetId">Spreadsheet ID</label>
            <input
              aria-invalid={!!errors.sourceSpreadsheetId}
              placeholder="1Hzf67exmplSpd7id..."
              type="text"
              {...register("sourceSpreadsheetId")}
            ></input>
            {!!errors.sourceSpreadsheetId && (
              <InputErrorHelper error={errors.sourceSpreadsheetId} />
            )}
          </div>
          <div className="form-control">
            <label htmlFor="accountSheetName">Sheet name? (default to "accounts")</label>
            <input
              aria-invalid={!!errors.accountSheetName}
              placeholder="accounts"
              type="text"
              {...register("accountSheetName")}
            ></input>
            {!!errors.accountSheetName && <InputErrorHelper error={errors.accountSheetName} />}
          </div>
          <br />

          {!!errors.root && <InputErrorHelper error={errors.root} />}
          <button disabled={!isValid} type="submit">
            Import
          </button>
        </form>
      </Card>
    </>
  );
}
